import { AttachmentInfoBarMessageId } from './AttachmentInfoBarMessageId';
import {
    InfoBarHostViewState,
    InfoBarCustomAction,
    InfoBarMessageRank,
    InfoBarMessageSource,
    InfoBarMessageViewState,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import AttachmentUploadQueueItem from '../../types/AttachmentUploadQueueItem';
import addInfoBarMessage from 'owa-info-bar/lib/actions/addInfoBarMessage';
import loc, { format } from 'owa-localize';
import {
    WarningFilesCanNotBeAttachedWithNamesGenericError,
    WarningSeveralFilesCanNotBeAttachedGenericError,
    WarningSingleFileCannotBeAttachedWithFallback,
    WarningFilesCannotBeAttachedFromThatMailbox,
    WarningSpecialCharactersAreNotSupported,
    TryAgain,
} from './showErrorMessagesForUploadFailures.locstring.json';

const MAX_NUMBER_OF_FAILED_ATTACHMENT_NAMES_IN_INFO_BAR = 4;

// TODO VSO 51804 - add cannot attach to self
export const enum AttachmentUploadFailureReason {
    GenericError,
    NonExistentMailbox,
    SpecialCharactersAreNotSupported,
}

export interface AttachmentUploadItemFailure {
    item: AttachmentUploadQueueItem;
    reason: AttachmentUploadFailureReason;
}

function groupBy<T>(
    list: T[],
    groupByProperty: (val: T) => string | number
): { [key: number]: T[] } {
    return list.reduce((groups, val) => {
        const prop = groupByProperty(val);
        if (!groups[prop]) {
            groups[prop] = [val];
        } else {
            groups[prop].push(val);
        }

        return groups;
    }, {} as { [key: number]: T[] });
}

function addMessageToInfoBar(
    infoMessageId: AttachmentInfoBarMessageId,
    message: string,
    infoBar: InfoBarHostViewState
) {
    addInfoBarMessage(infoBar, infoMessageId, [message]);
}

function showGenericErrorMessage(
    failures: AttachmentUploadItemFailure[],
    infoBar: InfoBarHostViewState
) {
    let message = null;
    let messageId = null;
    if (failures.length === 1 && failures[0].item.tryAgainCallback !== null) {
        const failure = failures[0];
        const fileName = failure.item.file.name;

        const messageString = format(loc(WarningSingleFileCannotBeAttachedWithFallback), fileName);
        const infoBarMessageViewState: InfoBarMessageViewState = {
            key: 'WarningFilesCanNotBeAttachedWithNamesGenericError',
            source: InfoBarMessageSource.Compose,
            rank: InfoBarMessageRank.Info,
            message: messageString,
            messageParts: [messageString],
        };
        const ignoreCustomAction: InfoBarCustomAction = {
            text: loc(TryAgain),
            action: () => failure.item.tryAgainCallback(),
        };
        infoBarMessageViewState.messageParts.push(ignoreCustomAction);
        addInfoBarMessage(
            infoBar,
            'WarningFilesCanNotBeAttachedWithNamesGenericError',
            (viewState: any) => infoBarMessageViewState
        );
        return;
    } else if (failures.length < MAX_NUMBER_OF_FAILED_ATTACHMENT_NAMES_IN_INFO_BAR) {
        const fileNames = failures.map(failure => failure.item.file.name).join(', ');
        messageId = 'WarningFilesCanNotBeAttachedWithNamesGenericError';
        message = format(loc(WarningFilesCanNotBeAttachedWithNamesGenericError), fileNames);
    } else {
        messageId = 'WarningSeveralFilesCanNotBeAttachedGenericError';
        message = format(loc(WarningSeveralFilesCanNotBeAttachedGenericError), failures.length);
    }

    addMessageToInfoBar(messageId, message, infoBar);
}

function showNonExistentMailboxErrorMessage(
    failures: AttachmentUploadItemFailure[],
    infoBar: InfoBarHostViewState
) {
    const message = loc(WarningFilesCannotBeAttachedFromThatMailbox);
    const messageId = 'WarningFilesCannotBeAttachedFromOtherMailboxes';
    addMessageToInfoBar(messageId, message, infoBar);
}

function showSpecialCharactersAreNotSupportedErrorMessage(
    failures: AttachmentUploadItemFailure[],
    infoBar: InfoBarHostViewState
) {
    const message = loc(WarningSpecialCharactersAreNotSupported);
    const messageId = 'WarningSpecialCharactersAreNotSupported';
    addMessageToInfoBar(messageId, message, infoBar);
}

export default function showErrorMessagesForUploadFailures(
    failures: AttachmentUploadItemFailure[],
    infoBar: InfoBarHostViewState
): void {
    // Creating a dictionary which has failures grouped by reason
    const groupedFailuresByReason = groupBy<AttachmentUploadItemFailure>(
        failures,
        failure => failure.reason
    );

    // Going each of the failure reason and showing message
    const failureReasons = Object.keys(groupedFailuresByReason);
    failureReasons.forEach(reason => {
        const failureGroup: AttachmentUploadItemFailure[] = groupedFailuresByReason[reason];
        switch (parseInt(reason)) {
            case AttachmentUploadFailureReason.GenericError:
                // Even though we have the infoBarHandler as part of the failure group
                // as usually it is the same so we are just passing in the first one
                showGenericErrorMessage(failureGroup, infoBar);
                break;
            case AttachmentUploadFailureReason.NonExistentMailbox:
                showNonExistentMailboxErrorMessage(failureGroup, infoBar);
                break;
            case AttachmentUploadFailureReason.SpecialCharactersAreNotSupported:
                showSpecialCharactersAreNotSupportedErrorMessage(failureGroup, infoBar);
                break;
        }
    });
}
